import React, { useEffect, useState, useCallback, useRef, memo } from "react"
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Container,
  Avatar,
  CardActionArea,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  CardActions,
  Divider,
  LinearProgress,
  Select,
  MenuItem,
} from "@material-ui/core"
import {
  Face,
  Security as SecurityIcon,
  LocationOn as LocationIcon,
  Close as CloseIcon,
  Devices as DevicesIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  ScreenLockPortrait,
  Book,
  AccessAlarms,
  NavigateBefore,
  NavigateNext,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from "@material-ui/icons"
import { useSelector } from "react-redux"
import { selectConjunto } from "src/redux/slices/complexSlice"
import { useSnackbar } from "notistack"
import TgSearch from "src/components/Buttons/TgSearch"
import { getAllBiometricDevices } from "src/services/admin/biometrics"
import axiosInstance from "src/utils/axios"
import moment from "moment"
import { getModulos } from "src/services/admin/resident"
import BiometricDevicesIndex from "./BiometricDevices"
import NewAndEditRostro from "./New/NewAndEditRostro"
import DeleteRostro from "./Delete/DeleteRostro"
import useStyles from "./useStyles"
import InformativeDialog from "./Dialogs/InformativeDialog"
import RostroDetailChips from "./RostroDetailChips"
import ReportFaces from "./ReportFaces"
import { withCacheBust } from "./imageUtils"

const getUnitNumber = destino => {
  if (!destino) return "Sin destino"
  return destino.split("/")[0]
}

// Genera la clave única para un conjunto de parámetros de búsqueda
const buildCacheKey = (pageNum, pageSz, search, deviceId, syncPending) =>
  `${pageNum}|${pageSz}|${search}|${deviceId}|${syncPending ? "1" : "0"}`

// ─── Card memoizada: solo re-renderiza si cambia el objeto rostro ────────────
const FaceCard = memo(
  ({
    item,
    classes,
    onView,
    conjunto,
    devices,
    onChanged,
    modulos,
    selectedDeviceFilter,
  }) => {
    const displaySrc = withCacheBust(item.imagen_url, item.fecha_actualizacion)

    return (
      <Card className={classes.professionalCard}>
        <CardActionArea onClick={() => onView(item)}>
          <Box className={classes.avatarContainer}>
            {item.imagen_url ? (
              <img
                key={displaySrc}
                src={displaySrc}
                alt={item.nombre}
                loading="lazy"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            ) : (
              <Avatar className={classes.professionalAvatar}>
                <Face style={{ fontSize: 60, color: "#64748b" }} />
              </Avatar>
            )}
          </Box>
        </CardActionArea>

        <CardContent className={classes.cardContent}>
          <Typography
            variant="h6"
            style={{
              fontWeight: "600",
              marginBottom: 8,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.nombre || "Sin nombre"}
          </Typography>

          <Box display="flex" alignItems="center" mb={1}>
            <BusinessIcon
              style={{ fontSize: 16, color: "#64748b", marginRight: 6 }}
            />
            <Typography variant="body2" style={{ color: "#64748b" }}>
              {item.identificacion || "Sin identificación"}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" mb={1}>
            <LocationIcon
              style={{ fontSize: 16, color: "#64748b", marginRight: 6 }}
            />
            <Typography variant="body2" style={{ color: "#64748b" }}>
              {getUnitNumber(item.destino)}
            </Typography>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <Box display="flex" alignItems="center">
              <DevicesIcon style={{ fontSize: 16, marginRight: 4 }} />
              <Typography variant="caption" style={{ fontWeight: "600" }}>
                {item.devices.length} dispositivo
                {item.devices.length !== 1 ? "s" : ""}
              </Typography>
            </Box>
            <Typography variant="caption" style={{ color: "#64748b" }}>
              {item.fecha_creacion
                ? moment(item.fecha_creacion).format("DD/MM/YYYY")
                : ""}
            </Typography>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {item.devices.slice(0, 2).map(device => (
              <Chip
                key={device.deviceId}
                label={device.deviceName}
                className={classes.deviceChip}
                style={{ backgroundColor: "#f1f5f9", color: "#475569" }}
              />
            ))}
            {item.devices.length > 2 && (
              <Chip
                label={`+${item.devices.length - 2} más`}
                className={classes.deviceChip}
                style={{ backgroundColor: "#e2e8f0", color: "#64748b" }}
              />
            )}
          </Box>
        </CardContent>

        <CardActions className={classes.cardActions}>
          <Box display="flex" gap={1}>
            <Tooltip title="Ver detalles">
              <IconButton
                className={classes.actionButton}
                onClick={() => onView(item)}
                size="medium"
                style={{ color: "#213759" }}
              >
                <ViewIcon />
              </IconButton>
            </Tooltip>

            <NewAndEditRostro
              conjunto={conjunto}
              selectedRostro={{
                ...item,
                pisos:
                  selectedDeviceFilter && item.devices.length > 0
                    ? item.devices[0].userInfo?.pisos || item.pisos
                    : item.pisos,
              }}
              devices={devices}
              setAllUniqueFaces={onChanged}
              allDevicesOriginal={item.devices}
              moduloBiometricosResidentes={modulos?.BiometricosResidentes}
            />

            <DeleteRostro
              selectedRostro={item}
              idConjunto={conjunto.id}
              onClose={() => {}}
              setAllUniqueFaces={onChanged}
            />
          </Box>
        </CardActions>
      </Card>
    )
  }
)

// ─── Componente principal ────────────────────────────────────────────────────
const FacesDirectory = () => {
  const classes = useStyles()
  const conjunto = useSelector(selectConjunto)
  const { enqueueSnackbar } = useSnackbar()

  const [viewMode, setViewMode] = useState("faces")
  const [faceFilterMode, setFaceFilterMode] = useState("all")
  const [devices, setDevices] = useState([])
  const [selectedDeviceFilter, setSelectedDeviceFilter] = useState("")

  const [facesData, setFacesData] = useState({
    faces: [],
    has_next: false,
    has_prev: false,
    total: null,
  })
  const [isFetching, setIsFetching] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)

  // Página 1-based (igual que la API)
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(8)

  // searched = texto en el input; committedSearch = lo que realmente dispara la API
  const [searched, setSearched] = useState("")
  const [committedSearch, setCommittedSearch] = useState("")

  // Caché de páginas en cliente: evita re-fetch de páginas ya visitadas
  // Se invalida al cambiar filtros/búsqueda o después de mutaciones
  const pageCache = useRef(new Map())
  const prefetchInFlight = useRef(new Set())

  const [showInstructionsDialog, setShowInstructionsDialog] = useState(false)
  const [openImageDialog, setOpenImageDialog] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [modulos, setModulos] = useState({})

  // Módulos
  useEffect(() => {
    const unsub = getModulos(
      { next: snap => setModulos(snap.data()) },
      conjunto.id
    )
    return unsub
  }, [conjunto.id])

  // Listener de dispositivos
  useEffect(() => {
    if (!conjunto?.id) return undefined
    const unsub = getAllBiometricDevices(conjunto.id, snapshot => {
      setDevices(
        snapshot.docs.map(doc => ({
          id: doc.id,
          rostroId: doc.id,
          type: "device",
          ...doc.data(),
        }))
      )
    })
    return unsub
  }, [conjunto?.id])

  // ── Fetch de una página (con caché de cliente) ────────────────────────────
  const fetchPage = useCallback(
    async (
      pageNum,
      { pageSz, search, deviceId, syncPending, transition = true } = {}
    ) => {
      if (!conjunto?.id) return

      const pSz = pageSz ?? rowsPerPage
      const srch = search ?? committedSearch
      const dev = deviceId ?? selectedDeviceFilter
      const sp = syncPending ?? faceFilterMode === "pending-sync"
      const cacheKey = buildCacheKey(pageNum, pSz, srch, dev, sp)

      // Cache hit local: instantáneo
      if (pageCache.current.has(cacheKey)) {
        setFacesData(pageCache.current.get(cacheKey))
        return
      }

      if (transition) setIsTransitioning(true)
      setIsFetching(true)

      try {
        const params = new URLSearchParams({
          page: pageNum,
          page_size: pSz,
          search: srch,
          device_id: dev,
          sync_pending: sp ? "true" : "false",
        })
        const { data } = await axiosInstance.get(
          `/api/biometrics/conjuntos/${conjunto.id}/rostros/list/?${params}`
        )
        pageCache.current.set(cacheKey, data)
        setFacesData(data)
        setHasLoaded(true)

        // Prefetch inmediato de la siguiente si existe
        if (data.has_next && !srch) {
          const nextKey = buildCacheKey(pageNum + 1, pSz, srch, dev, sp)
          if (
            !pageCache.current.has(nextKey) &&
            !prefetchInFlight.current.has(nextKey)
          ) {
            prefetchInFlight.current.add(nextKey)
            const nextParams = new URLSearchParams({
              page: pageNum + 1,
              page_size: pSz,
              search: srch,
              device_id: dev,
              sync_pending: sp ? "true" : "false",
            })
            axiosInstance
              .get(
                `/api/biometrics/conjuntos/${conjunto.id}/rostros/list/?${nextParams}`
              )
              .then(({ data: nd }) => pageCache.current.set(nextKey, nd))
              .catch(() => {})
              .finally(() => prefetchInFlight.current.delete(nextKey))
          }
        }
      } catch {
        enqueueSnackbar("Error al cargar rostros", { variant: "error" })
      } finally {
        setIsFetching(false)
        setIsTransitioning(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      conjunto?.id,
      rowsPerPage,
      committedSearch,
      selectedDeviceFilter,
      faceFilterMode,
    ]
  )

  // Carga inicial y al cambiar filtros (no incluye searched — solo committedSearch)
  useEffect(() => {
    if (viewMode === "faces") {
      pageCache.current.clear()
      prefetchInFlight.current.clear()
      setPage(1)
      fetchPage(1, { transition: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    conjunto?.id,
    viewMode,
    committedSearch,
    selectedDeviceFilter,
    faceFilterMode,
    rowsPerPage,
  ])

  // ── Cambio de página (prev / next) ────────────────────────────────────────
  const goToPrev = useCallback(() => {
    const prev = page - 1
    if (prev < 1) return
    setPage(prev)
    window.scrollTo(0, 0)
    fetchPage(prev)
  }, [page, fetchPage])

  const goToNext = useCallback(() => {
    if (!facesData.has_next) return
    const next = page + 1
    setPage(next)
    window.scrollTo(0, 0)
    fetchPage(next)
  }, [page, facesData.has_next, fetchPage])

  const handleChangeRowsPerPage = useCallback(
    event => {
      const newSz = parseInt(event.target.value, 10)
      setRowsPerPage(newSz)
      setPage(1)
      pageCache.current.clear()
      prefetchInFlight.current.clear()
      fetchPage(1, { pageSz: newSz })
    },
    [fetchPage]
  )

  // ── Búsqueda manual (botón o Enter) ──────────────────────────────────────
  // Solo actualiza el input — NO dispara búsqueda
  const handleSearchChange = useCallback(val => setSearched(val), [])

  // Dispara la búsqueda (botón o Enter), incluso si el texto no cambió
  const commitSearch = useCallback(() => {
    pageCache.current.clear()
    prefetchInFlight.current.clear()
    setPage(1)
    if (searched === committedSearch) {
      fetchPage(1, { search: searched, transition: false })
      return
    }
    setCommittedSearch(searched)
  }, [searched, committedSearch, fetchPage])

  const handleSearchKeyDown = useCallback(
    e => {
      if (e.key === "Enter") commitSearch()
    },
    [commitSearch]
  )

  const cancelSearch = useCallback(() => {
    setSearched("")
    pageCache.current.clear()
    prefetchInFlight.current.clear()
    setCommittedSearch("")
    setPage(1)
  }, [])

  // ── Filtros ───────────────────────────────────────────────────────────────
  const handleDeviceFilterChange = useCallback(e => {
    pageCache.current.clear()
    prefetchInFlight.current.clear()
    setSelectedDeviceFilter(e.target.value)
    setPage(1)
  }, [])

  // ── Después de mutación: invalidar caché y refrescar ──────────────────────
  const refreshFaces = useCallback(() => {
    pageCache.current.clear()
    prefetchInFlight.current.clear()
    setPage(1)
    fetchPage(1, { showTransition: false })
  }, [fetchPage])

  // ── Vista ─────────────────────────────────────────────────────────────────
  const toggleViewMode = useCallback(() => {
    const next = viewMode === "devices" ? "faces" : "devices"
    setViewMode(next)
    if (next === "faces") setFaceFilterMode("all")
    setSearched("")
    setCommittedSearch("")
    setPage(1)
    pageCache.current.clear()
  }, [viewMode])

  const togglePendingSyncFaces = useCallback(() => {
    setViewMode("faces")
    setFaceFilterMode(prev => {
      if (viewMode === "devices") return "pending-sync"
      return prev === "pending-sync" ? "all" : "pending-sync"
    })
    setSearched("")
    setCommittedSearch("")
    setPage(1)
    pageCache.current.clear()
    prefetchInFlight.current.clear()
  }, [viewMode])

  // ── Detalle de imagen ─────────────────────────────────────────────────────
  const handleImageClick = useCallback(face => {
    setSelectedImage({
      url: withCacheBust(face.imagen_url, face.fecha_actualizacion),
      nombre: face.nombre || "Sin nombre",
      identificacion: face.identificacion || "Sin identificación",
      destino: face.destino || "Sin destino",
      tipo: face.tipo || "",
      estado: face.estado || "",
      pisos: face.pisos || [],
      pin: face.pin || "",
      tarjeta: face.tarjeta || [],
      devices: face.devices || [],
      fechaCreacion: face.fecha_creacion,
    })
    setOpenImageDialog(true)
  }, [])

  const isFirstLoad = !hasLoaded && isFetching
  const paginatedData = facesData.faces || []

  if (!conjunto) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" my={5}>
          <Paper
            elevation={3}
            style={{
              padding: "2rem",
              textAlign: "center",
              borderRadius: "16px",
            }}
          >
            <SecurityIcon
              style={{ fontSize: 60, color: "#ccc", marginBottom: "1rem" }}
            />
            <Typography variant="h6">
              Seleccione un conjunto para continuar
            </Typography>
          </Paper>
        </Box>
      </Container>
    )
  }

  return (
    <div className={classes.root}>
      <Container maxWidth="xl">
        <Paper className={classes.explorerContainer} elevation={3}>
          {/* ── Barra superior ──────────────────────────────────────────── */}
          <Box className={classes.toolbar}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              color="#092147"
            >
              <Face
                style={{ fontSize: 30, marginBottom: "1rem", opacity: 0.9 }}
              />
              <Typography
                variant="h4"
                align="center"
                style={{ fontWeight: "bold", marginBottom: "0.5rem" }}
              >
                Dispositivos Biométricos
              </Typography>
              <Typography variant="p" align="center" style={{ opacity: 0.9 }}>
                Sistema de Control de Acceso Inteligente
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={
                    viewMode === "devices" ? <Face /> : <ScreenLockPortrait />
                  }
                  onClick={toggleViewMode}
                  style={{
                    textTransform: "none",
                    fontWeight: "600",
                    marginRight: "0.6em",
                  }}
                >
                  {viewMode === "devices" ? "Ver Rostros" : "Ver Dispositivos"}
                </Button>

                <Tooltip title="Ver instrucciones de captura de rostro">
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<Book />}
                    onClick={() => setShowInstructionsDialog(true)}
                    style={{
                      textTransform: "none",
                      fontWeight: "600",
                      marginRight: "0.6em",
                    }}
                  >
                    Ver Instrucciones
                  </Button>
                </Tooltip>

                <Tooltip
                  title={
                    faceFilterMode === "pending-sync"
                      ? "Ver todos los rostros"
                      : "Pendientes de sincronización"
                  }
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={
                      faceFilterMode === "pending-sync" ? (
                        <Face />
                      ) : (
                        <AccessAlarms />
                      )
                    }
                    onClick={togglePendingSyncFaces}
                    style={{ textTransform: "none", fontWeight: "600" }}
                  >
                    {faceFilterMode === "pending-sync"
                      ? "Ver Rostros"
                      : "Pendientes de sincronización"}
                  </Button>
                </Tooltip>
              </Box>

              <Box display="flex" gap={2} alignItems="center">
                {viewMode === "faces" && modulos?.ExportarRostros !== false && (
                  <ReportFaces conjunto={conjunto} />
                )}
                <NewAndEditRostro
                  conjunto={conjunto}
                  devices={devices}
                  setAllUniqueFaces={refreshFaces}
                  moduloBiometricosResidentes={modulos?.BiometricosResidentes}
                />
              </Box>
            </Box>
          </Box>

          {/* ── Barra de progreso ───────────────────────────────────────── */}
          {(isFetching || isTransitioning) && (
            <LinearProgress style={{ height: 3 }} />
          )}

          {/* ── Barra de filtros ───────────────────────────────────────── */}
          <Box
            style={{
              padding: "16px 24px",
              borderBottom: "1px solid #e2e8f0",
              backgroundColor: "#f8fafc",
            }}
          >
            {/* Fila de controles */}
            <Box
              display="flex"
              alignItems="stretch"
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                backgroundColor: "white",
                overflow: "hidden",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              }}
            >
              {/* Ícono de búsqueda */}
              <Box
                display="flex"
                alignItems="center"
                px={2}
                style={{ color: "#94a3b8", borderRight: "1px solid #f1f5f9" }}
              >
                <SearchIcon fontSize="small" />
              </Box>

              {/* Input de búsqueda */}
              <Box
                flex={1}
                onKeyDown={handleSearchKeyDown}
                style={{ minWidth: 0 }}
              >
                <TgSearch
                  placeholder={
                    viewMode === "devices"
                      ? "Buscar dispositivo por nombre, ubicación, IP..."
                      : "Buscar por nombre, identificación, destino..."
                  }
                  value={searched}
                  onChange={handleSearchChange}
                  onCancelSearch={cancelSearch}
                  style={{ border: "none", boxShadow: "none" }}
                />
              </Box>

              {/* Separador + filtro de dispositivo */}
              {viewMode === "faces" && devices.length > 0 && (
                <>
                  <Box
                    style={{
                      width: 1,
                      backgroundColor: "#e2e8f0",
                      margin: "8px 0",
                    }}
                  />
                  <Box
                    display="flex"
                    alignItems="center"
                    px={1.5}
                    style={{ gap: 6, minWidth: 180 }}
                  >
                    <FilterIcon
                      fontSize="small"
                      style={{ color: "#94a3b8", flexShrink: 0 }}
                    />
                    <Select
                      value={selectedDeviceFilter}
                      onChange={handleDeviceFilterChange}
                      disableUnderline
                      displayEmpty
                      style={{
                        fontSize: 13,
                        color: selectedDeviceFilter ? "#1e293b" : "#94a3b8",
                        minWidth: 140,
                      }}
                    >
                      <MenuItem value="">
                        <em style={{ fontStyle: "normal", color: "#94a3b8" }}>
                          Todos los dispositivos
                        </em>
                      </MenuItem>
                      {devices.map(device => (
                        <MenuItem
                          key={device.id}
                          value={device.rostroId}
                          style={{ fontSize: 13 }}
                        >
                          {device.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </>
              )}

              {/* Separador + botón buscar */}
              <Box style={{ width: 1, backgroundColor: "#e2e8f0" }} />
              <Button
                variant="text"
                onClick={commitSearch}
                disabled={isFetching}
                style={{
                  textTransform: "none",
                  fontWeight: "600",
                  color: "white",
                  backgroundColor: "#092147",
                  borderRadius: 0,
                  padding: "10px 24px",
                  minWidth: 110,
                  letterSpacing: 0.3,
                }}
              >
                {isFetching ? (
                  <CircularProgress size={16} style={{ color: "white" }} />
                ) : (
                  "Buscar"
                )}
              </Button>
            </Box>

            {/* Chips de filtros activos + contador */}
            <Box
              mt={1.5}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box
                display="flex"
                alignItems="center"
                style={{ gap: 6, flexWrap: "wrap" }}
              >
                {committedSearch && (
                  <Chip
                    size="small"
                    label={`"${committedSearch}"`}
                    onDelete={cancelSearch}
                    style={{
                      backgroundColor: "#eff6ff",
                      color: "#1d4ed8",
                      fontWeight: 500,
                      fontSize: 12,
                    }}
                  />
                )}
                {selectedDeviceFilter && (
                  <Chip
                    size="small"
                    label={
                      devices.find(d => d.rostroId === selectedDeviceFilter)
                        ?.nombre || selectedDeviceFilter
                    }
                    onDelete={() => {
                      pageCache.current.clear()
                      prefetchInFlight.current.clear()
                      setSelectedDeviceFilter("")
                      setPage(1)
                    }}
                    icon={<DevicesIcon style={{ fontSize: 14 }} />}
                    style={{
                      backgroundColor: "#f0fdf4",
                      color: "#166534",
                      fontWeight: 500,
                      fontSize: 12,
                    }}
                  />
                )}
                {faceFilterMode === "pending-sync" && (
                  <Chip
                    size="small"
                    label="Pendientes de sincronización"
                    style={{
                      backgroundColor: "#fff7ed",
                      color: "#c2410c",
                      fontWeight: 500,
                      fontSize: 12,
                    }}
                  />
                )}
                {(committedSearch || selectedDeviceFilter) && (
                  <Button
                    size="small"
                    onClick={() => {
                      setSearched("")
                      setCommittedSearch("")
                      pageCache.current.clear()
                      prefetchInFlight.current.clear()
                      setSelectedDeviceFilter("")
                      setPage(1)
                    }}
                    style={{
                      textTransform: "none",
                      fontSize: 12,
                      color: "#64748b",
                      padding: "2px 8px",
                      minWidth: 0,
                    }}
                  >
                    Limpiar todo
                  </Button>
                )}
              </Box>
              <Typography
                variant="caption"
                style={{
                  color: "#94a3b8",
                  whiteSpace: "nowrap",
                  marginLeft: 8,
                }}
              >
                {isFetching && !hasLoaded
                  ? "Cargando..."
                  : `Pág. ${page} · ${paginatedData.length} elemento${paginatedData.length !== 1 ? "s" : ""}${facesData.total != null ? ` de ${facesData.total}` : ""}`}
              </Typography>
            </Box>
          </Box>

          {/* ── Contenido principal ────────────────────────────────────── */}
          {viewMode !== "devices" ? (
            <Box className={classes.contentArea} position="relative">
              {/* Overlay solo en la primera carga */}
              {isFirstLoad && (
                <Box className={classes.loadingOverlay}>
                  <Box textAlign="center">
                    <CircularProgress size={40} style={{ marginBottom: 16 }} />
                    <Typography variant="body2" style={{ color: "#64748b" }}>
                      Cargando datos...
                    </Typography>
                  </Box>
                </Box>
              )}

              {!isFirstLoad && paginatedData.length === 0 ? (
                <Box className={classes.emptyState}>
                  <Face
                    style={{ fontSize: 80, color: "#cbd5e0", marginBottom: 20 }}
                  />
                  <Typography
                    variant="h5"
                    style={{ marginBottom: 12, fontWeight: "600" }}
                  >
                    {searched
                      ? "No se encontraron resultados"
                      : "No hay datos disponibles"}
                  </Typography>
                  <Typography variant="body1" style={{ marginBottom: 20 }}>
                    {searched
                      ? `No hay personas que coincidan con "${searched}"`
                      : "No hay rostros registrados"}
                  </Typography>
                  {searched && (
                    <Button
                      variant="contained"
                      onClick={cancelSearch}
                      style={{
                        backgroundColor: "#3b82f6",
                        color: "white",
                        textTransform: "none",
                        fontWeight: "600",
                      }}
                    >
                      Limpiar búsqueda
                    </Button>
                  )}
                </Box>
              ) : (
                /* Datos anteriores visibles con opacidad reducida mientras
                   carga la siguiente página (sin pantalla en blanco) */
                <Box
                  style={{
                    opacity: isTransitioning ? 0.5 : 1,
                    transition: "opacity 0.15s ease",
                  }}
                >
                  <Grid container spacing={3}>
                    {paginatedData.map(item => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        xl={3}
                        key={item.uniqueKey}
                      >
                        <FaceCard
                          item={item}
                          classes={classes}
                          onView={handleImageClick}
                          conjunto={conjunto}
                          devices={devices}
                          onChanged={refreshFaces}
                          modulos={modulos}
                          selectedDeviceFilter={selectedDeviceFilter}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          ) : (
            <BiometricDevicesIndex
              moduloBiometricosResidentes={modulos?.BiometricosResidentes}
            />
          )}

          {/* ── Paginación ─────────────────────────────────────────────── */}
          {viewMode === "faces" && hasLoaded && (
            <Box
              className={classes.paginationContainer}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={2}
              style={{
                borderTop: "1px solid #e2e8f0",
                backgroundColor: "white",
                minHeight: 52,
              }}
            >
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Typography variant="caption" style={{ color: "#64748b" }}>
                  Elementos por página:
                </Typography>
                <Select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  disableUnderline
                  variant="standard"
                  style={{ fontSize: 14, color: "#374151" }}
                >
                  {[8, 16, 32, 64].map(n => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                <Typography
                  variant="caption"
                  style={{ color: "#64748b", marginRight: 8 }}
                >
                  Página {page}
                </Typography>
                <Tooltip title="Página anterior">
                  <span>
                    <IconButton
                      size="small"
                      onClick={goToPrev}
                      disabled={!facesData.has_prev || isFetching}
                    >
                      <NavigateBefore />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Página siguiente">
                  <span>
                    <IconButton
                      size="small"
                      onClick={goToNext}
                      disabled={!facesData.has_next || isFetching}
                    >
                      <NavigateNext />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          )}
        </Paper>

        {/* ── Pie de página ─────────────────────────────────────────── */}
        <Box className={classes.footer}>
          <Typography className={classes.disclaimerText}>
            <strong>Nota Importante:</strong> Se le recuerda a la administración
            que los datos biométricos deben ser utilizados de manera correcta y
            conforme a las leyes de protección de datos vigentes. El sistema no
            se responsabiliza por el mal uso, acceso no autorizado o violaciones
            a la privacidad que puedan derivarse del manejo inadecuado de esta
            información. Es responsabilidad exclusiva de la administración
            garantizar el uso ético y legal de los datos almacenados.
          </Typography>
        </Box>

        <InformativeDialog
          open={showInstructionsDialog}
          onClose={() => setShowInstructionsDialog(false)}
          onContinue={() => setShowInstructionsDialog(false)}
          isEditing={false}
          showCheckbox={false}
        />

        {/* ── Dialog de detalle ─────────────────────────────────────── */}
        <Dialog
          open={openImageDialog}
          onClose={() => setOpenImageDialog(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ style: { borderRadius: "16px" } }}
        >
          <DialogTitle
            style={{
              backgroundColor: "#092147",
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography
                  variant="h5"
                  style={{ fontWeight: "600", marginBottom: 4, color: "white" }}
                >
                  {selectedImage?.nombre}
                </Typography>
                <Typography variant="body2" style={{ color: "white" }}>
                  {selectedImage?.identificacion} •{" "}
                  {getUnitNumber(selectedImage?.destino)}
                </Typography>
              </Box>
              <IconButton
                onClick={() => setOpenImageDialog(false)}
                style={{ color: "white" }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent style={{ padding: 0 }}>
            <Grid container>
              <Grid item xs={12} md={5}>
                <Box
                  p={3}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  style={{ minHeight: "400px" }}
                >
                  {selectedImage?.url ? (
                    <img
                      key={selectedImage.url}
                      src={selectedImage.url}
                      alt={selectedImage.nombre}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "500px",
                        objectFit: "contain",
                        borderRadius: "12px",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                      }}
                    />
                  ) : (
                    <Avatar style={{ width: 200, height: 200 }}>
                      <Face style={{ fontSize: 80 }} />
                    </Avatar>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={7}>
                <Box p={3}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    style={{ fontWeight: "600", color: "#1f2937" }}
                  >
                    Información Personal
                  </Typography>
                  <Divider style={{ marginBottom: 16 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box mb={1}>
                        <Typography
                          variant="subtitle2"
                          style={{ color: "#64748b", fontWeight: "600" }}
                        >
                          Nombre
                        </Typography>
                        <Typography variant="body1">
                          {selectedImage?.nombre}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box mb={1}>
                        <Typography
                          variant="subtitle2"
                          style={{ color: "#64748b", fontWeight: "600" }}
                        >
                          Identificación
                        </Typography>
                        <Typography variant="body1">
                          {selectedImage?.identificacion}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box mb={1}>
                        <Typography
                          variant="subtitle2"
                          style={{ color: "#64748b", fontWeight: "600" }}
                        >
                          Unidad
                        </Typography>
                        <Typography variant="body1">
                          {getUnitNumber(selectedImage?.destino) || "—"}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box mb={1}>
                        <Typography
                          variant="subtitle2"
                          style={{ color: "#64748b", fontWeight: "600" }}
                        >
                          Tipo / Estado
                        </Typography>
                        <Box display="flex" style={{ gap: 4 }} mt={0.5}>
                          {selectedImage?.tipo && (
                            <Chip
                              size="small"
                              label={selectedImage.tipo}
                              style={{
                                backgroundColor: "#e0f2fe",
                                color: "#0369a1",
                                fontSize: "0.7rem",
                                height: 20,
                              }}
                            />
                          )}
                          {selectedImage?.estado && (
                            <Chip
                              size="small"
                              label={selectedImage.estado}
                              style={{
                                backgroundColor:
                                  selectedImage.estado === "activo"
                                    ? "#dcfce7"
                                    : "#fee2e2",
                                color:
                                  selectedImage.estado === "activo"
                                    ? "#166534"
                                    : "#991b1b",
                                fontSize: "0.7rem",
                                height: 20,
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Grid>

                    {selectedImage?.pisos?.length > 0 && (
                      <Grid item xs={12}>
                        <Box mb={1}>
                          <Typography
                            variant="subtitle2"
                            style={{
                              color: "#64748b",
                              fontWeight: "600",
                              marginBottom: 4,
                            }}
                          >
                            Pisos de Acceso ({selectedImage.pisos.length})
                          </Typography>
                          <Box
                            display="flex"
                            flexWrap="wrap"
                            style={{ gap: 4 }}
                          >
                            {[...selectedImage.pisos]
                              .sort((a, b) => a - b)
                              .map(piso => (
                                <Chip
                                  key={piso}
                                  size="small"
                                  label={
                                    piso < 0
                                      ? `Sub ${Math.abs(piso)}`
                                      : `Piso ${piso}`
                                  }
                                  style={{
                                    backgroundColor:
                                      piso < 0 ? "#fef9c3" : "#f1f5f9",
                                    color: piso < 0 ? "#854d0e" : "#475569",
                                    fontSize: "0.65rem",
                                    height: 20,
                                  }}
                                />
                              ))}
                          </Box>
                        </Box>
                      </Grid>
                    )}

                    {(selectedImage?.pin ||
                      selectedImage?.tarjeta?.length > 0) && (
                      <Grid item xs={12}>
                        <Box
                          p={1.5}
                          style={{
                            backgroundColor: "#f8fafc",
                            borderRadius: 8,
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            style={{
                              color: "#64748b",
                              fontWeight: "600",
                              marginBottom: 6,
                            }}
                          >
                            Métodos de Acceso Adicionales
                          </Typography>
                          <RostroDetailChips rostro={selectedImage} />
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  <Box mt={3}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      style={{ fontWeight: "600", color: "#1f2937" }}
                    >
                      Dispositivos Registrados (
                      {selectedImage?.devices?.length || 0})
                    </Typography>
                    <Divider style={{ marginBottom: 16 }} />
                    <Box maxHeight={300} style={{ overflowY: "auto" }}>
                      {selectedImage?.devices?.map(device => (
                        <Box
                          key={`dialog-${device.deviceId}`}
                          mb={2}
                          p={2}
                          style={{
                            backgroundColor: "#f8fafc",
                            borderRadius: "8px",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            style={{
                              fontWeight: "600",
                              marginBottom: 4,
                              color: "#1f2937",
                            }}
                          >
                            {device.deviceName}
                          </Typography>
                          <Typography
                            variant="body2"
                            style={{ color: "#64748b", marginBottom: 2 }}
                          >
                            📍 {device.deviceUbicacion}
                          </Typography>
                          <Typography
                            variant="body2"
                            style={{ color: "#64748b", marginBottom: 2 }}
                          >
                            🏢 {device.deviceMarca}
                          </Typography>
                          <Typography
                            variant="caption"
                            style={{ color: "#64748b" }}
                          >
                            IP: {device.deviceIp}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions
            style={{ padding: "16px 24px", backgroundColor: "#f8fafc" }}
          >
            <Button
              onClick={() => setOpenImageDialog(false)}
              style={{ textTransform: "none" }}
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  )
}

export default FacesDirectory
